<template>
  <div id="staff-list" class="professional-nomination-list">
    <!-- Professional Main Card Container -->
    <v-card class="main-container" elevation="2">
      <!-- Professional Header Section -->
      <v-card-title class="professional-header pa-6">
        <div class="header-content">
          <div class="title-section">
            <div class="main-title-wrapper">
              <v-icon size="32" color="#1976d2" class="mr-3">mdi-account-star</v-icon>
              <div class="title-text">
                <h1 class="page-main-title">{{ PageTitle }}</h1>
                <div class="year-badge">
                  <v-chip color="#1976d2" text-color="white" small>
                    <v-icon left size="14">mdi-calendar</v-icon>
                    {{ YearName }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>

          <div class="action-section">
            <v-btn
              v-if="refreshButtonFlag"
              color="#1976d2"
              elevation="2"
              rounded
              outlined
              class="refresh-btn"
              @click.prevent="refreshPageData"
            >
              <v-icon left size="18">mdi-refresh</v-icon>
              Refresh Data
            </v-btn>
          </div>
        </div>

        <!-- Professional Subtitle Section -->
        <div class="subtitle-section mt-4" v-if="PageDescription">
          <div class="subtitle-wrapper">
            <v-icon size="20" color="#666" class="mr-2">mdi-information-outline</v-icon>
            <p class="page-description">{{ PageDescription }}</p>
          </div>
        </div>
      </v-card-title>

      <!-- Professional Content Container -->
      <v-card-text class="main-content pa-6">
        <!-- Professional Loading Section -->
        <div class="loading-section" v-if="LoadingFlag">
          <div class="loading-wrapper">
            <v-progress-circular
              indeterminate
              color="#1976d2"
              size="48"
              width="4"
            ></v-progress-circular>
            <p class="loading-text mt-4">Loading nomination data...</p>
          </div>
        </div>

        <!-- Dashboard Cards Component -->
        <nomination-dashboard-cards
          v-if="!LoadingFlag"
          :dashboardInfo="memberData"
        />

        <!-- Professional Information Sections -->
        <div class="info-sections-container" v-if="!LoadingFlag">
          <v-row>
            <!-- Important Requirements Section -->
            <v-col cols="12" lg="6">
              <v-card class="info-card requirements-card" elevation="2">
                <v-card-title class="info-card-header">
                  <v-icon color="#e74c3c" size="24" class="mr-3">mdi-alert-circle</v-icon>
                  <span class="info-card-title">Important Guidelines</span>
                </v-card-title>
                <v-card-text class="info-card-content">
                  <div class="requirements-list">
                    <div class="requirement-item">
                      <div class="requirement-number">1</div>
                      <div class="requirement-text">Member should have completed desired posting requirements</div>
                    </div>
                    <div class="requirement-item">
                      <div class="requirement-number">2</div>
                      <div class="requirement-text">Continuous membership as per constitution guidelines</div>
                    </div>
                    <div class="requirement-item">
                      <div class="requirement-number">3</div>
                      <div class="requirement-text">Profile information must be updated and verified</div>
                    </div>
                    <div class="requirement-item">
                      <div class="requirement-number">4</div>
                      <div class="requirement-text">Applications must be submitted within deadline</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Application Process Section -->
            <v-col cols="12" lg="6">
              <v-card class="info-card process-card" elevation="2">
                <v-card-title class="info-card-header">
                  <v-icon color="#3498db" size="24" class="mr-3">mdi-clipboard-list</v-icon>
                  <span class="info-card-title">Review Process</span>
                </v-card-title>
                <v-card-text class="info-card-content">
                  <div class="process-steps">
                    <div class="process-step">
                      <div class="step-number">1</div>
                      <div class="step-text">Application submitted by member</div>
                    </div>
                    <div class="process-step">
                      <div class="step-number">2</div>
                      <div class="step-text">LO certification and approval</div>
                    </div>
                    <div class="process-step">
                      <div class="step-number">3</div>
                      <div class="step-text">NHQ review and final approval</div>
                    </div>
                    <div class="process-step">
                      <div class="step-number">4</div>
                      <div class="step-text">Notification to member and LO</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Applications Section -->
        <div class="applications-section" v-if="!LoadingFlag">
          <div class="section-header">
            <div class="section-title">
              <v-icon color="#1976d2" class="mr-2">mdi-account-group</v-icon>
              <span>Nomination Applications</span>
            </div>
            <div class="applications-count">
              <v-chip color="#1976d2" text-color="white" class="count-chip">
                {{ rows.length }} applications found
              </v-chip>
            </div>
          </div>

          <!-- Member Cards Grid -->
          <div class="member-cards-container" v-if="rows.length > 0">
            <v-data-iterator
              :items="rows"
              :items-per-page="-1"
              hide-default-footer
              :search="search"
            >
              <template v-slot:default="props">
                <v-row class="member-cards-grid">
                  <v-col
                    v-for="item in props.items"
                    :key="item.YearwiseLomId"
                    cols="12"
                    sm="6"
                    md="6"
                    lg="4"
                    xl="3"
                    class="member-card-col"
                  >
                    <v-card class="nomination-card d-flex flex-column justify-space-between" elevation="6">
                      <!-- Professional Header -->
                      <v-card-title
                        class="nomination-header pa-4 white--text"
                        style="background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%); border-radius: 8px 8px 0 0;"
                      >
                        <div class="text-center" style="width: 100%;">
                          <div class="text-h6 font-weight-bold text-center">
                            Member Application
                          </div>
                        </div>
                      </v-card-title>

                      <!-- Member Header -->
                      <div class="member-header pa-4" style="background-color: #fafafa;">
                        <div class="member-avatar-section text-center">
                          <v-avatar size="80" class="member-avatar mb-3">
                            <img
                              :src="item.MemberImage"
                              :alt="item.MemberFullname"
                            />
                          </v-avatar>
                        </div>
                        <div class="member-basic-info text-center">
                          <h3 class="member-name">{{ item.MemberName }}</h3>
                          <p class="member-id">{{ item.MemberId }}</p>
                          <p class="member-designation">{{ item.DesignationName }} Nomination</p>
                        </div>
                      </div>

                      <!-- Member Status -->
                      <div class="member-status-section pa-4">
                        <div class="status-row">
                          <span class="status-label">Application</span>
                          <v-chip
                            :color="item.NominationStatusColor"
                            dark
                            small
                            class="status-chip"
                          >
                            {{ item.NominationStatusTxt }}
                          </v-chip>
                        </div>
                        <div class="status-row">
                          <span class="status-label">LO Certification</span>
                          <v-chip
                            :color="item.PresidentStatusColor"
                            dark
                            small
                            class="status-chip"
                          >
                            {{ item.PresidentStatusTxt }}
                          </v-chip>
                        </div>
                        <div class="status-row">
                          <span class="status-label">NHQ Status</span>
                          <v-chip
                            :color="item.VerifyStatusColor"
                            dark
                            small
                            class="status-chip"
                          >
                            {{ item.VerifyStatusTxt }}
                          </v-chip>
                        </div>
                      </div>

                      <!-- Action Buttons -->
                      <div class="member-actions pa-4" style="background-color: white; border-top: 1px solid #e0e0e0;">
                        <v-row no-gutters justify="center" class="action-buttons-row">
                          <v-col cols="auto" class="mx-1 mb-2" v-if="item.EditFlag">
                            <v-btn
                              small
                              color="warning"
                              class="action-btn edit-btn"
                              @click="editData(item)"
                            >
                              <v-icon small left>mdi-pencil</v-icon>
                              Edit
                            </v-btn>
                          </v-col>
                          <v-col cols="auto" class="mx-1 mb-2" v-if="item.DeleteFlag">
                            <v-btn
                              small
                              color="error"
                              class="action-btn delete-btn"
                              @click="deleteConfirmAlert(item)"
                            >
                              <v-icon small left>mdi-delete</v-icon>
                              Delete
                            </v-btn>
                          </v-col>
                          <v-col cols="auto" class="mx-1 mb-2" v-if="item.ApprovalFlag">
                            <v-btn
                              small
                              color="primary"
                              class="action-btn approve-btn"
                              @click="previewData(item)"
                            >
                              <v-icon small left>mdi-check-circle</v-icon>
                              Approval
                            </v-btn>
                          </v-col>
                            <!-- Download Button -->
                          <v-col
                            cols="auto"
                            class="mx-1 mb-2"
                            v-if="item.NominationDownloadFlag"
                          >
                            <v-btn
                              color="success"
                              class="action-btn white--text"
                              :href="item.ApplicationPath"
                              target="_blank"
                              elevation="2"
                              small
                            >
                              <v-icon left size="16" color="white"
                                >mdi-download</v-icon
                              >
                              Download
                            </v-btn>
                          </v-col>
                        </v-row>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </template>
            </v-data-iterator>
          </div>

          <!-- Empty State -->
          <div class="empty-state" v-else>
            <div class="empty-content">
              <v-icon size="80" color="grey lighten-2">mdi-account-off</v-icon>
              <h3 class="empty-title">No Applications Found</h3>
              <p class="empty-message">There are currently no nomination applications to display.</p>
            </div>
          </div>
        </div>

        <!-- Dialogs (keeping existing functionality) -->
        <v-dialog
          v-model="addRecordPrompt"
          persistent
          fullscreen
          hide-overlay
          transition="dialog-bottom-transition"
        >
          <nominate-member-create
            :addRecordPrompt="editRecordPrompt"
            :recordData="selectedData"
            :ZoneCode="ZoneCode"
            :LomCode="LomCode"
            @hideAddRecordPrompt="hideAddRecordPrompt"
            v-if="addRecordPrompt"
          ></nominate-member-create>
        </v-dialog>

        <v-dialog
          v-model="editRecordPrompt"
          persistent
          fullscreen
          hide-overlay
          transition="dialog-bottom-transition"
        >
          <nominate-member-edit
            :editRecordPrompt="editRecordPrompt"
            :recordData="selectedData"
            :LomCode="LomCode"
            @hideEditRecordPrompt="hideEditRecordPrompt"
            v-if="editRecordPrompt"
          ></nominate-member-edit>
        </v-dialog>

        <v-dialog v-model="EditRecordPrompt" persistent max-width="70%">
          <voting-description
            v-if="EditRecordPrompt"
            :recordData="VotingData"
            @hideDialog="hideAddRecordPrompt"
          ></voting-description>
        </v-dialog>

        <v-dialog v-model="previewRecordPrompt" max-width="75%">
          <nominate-member-president-approval
            pageDescription=" Nominate Member Approval"
            :showDialog="previewRecordPrompt"
            @hideDialog="hidePreviewRecordPrompt($event)"
            :row="selectedData"
            v-if="previewRecordPrompt"
          ></nominate-member-president-approval>
        </v-dialog>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import NominateMemberEdit from "@/view/pages/erp/events/nominate-member/NominateMemberEdit.vue";
import Swal from "sweetalert2";
import NominateMemberCreate from "@/view/pages/erp/events/nominate-member/NominateMemberCreate.vue";
import VotingCount from "@/view/pages/erp/lom/lom-information/VotingCount.vue";
import NominateMemberPresidentApproval from "@/view/pages/erp/events/nominate-member/NominateMemberPresidentApproval.vue";
import NominationDashboardCards from "@/view/components/nomination/NominationDashboardCards.vue";

export default {
  mixins: [common],
  components: {
    NominateMemberEdit,
    NominateMemberCreate,
    VotingCount,
    NominateMemberPresidentApproval,
    NominationDashboardCards,
  },
  data() {
    return {
      valid1: true,
      valid2: true,

      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: 0,

      refreshButtonFlag: true,
      floatButtonFlag: false,
      records: [],
      rows: [],
      tableColumns1: [],
      selected: [],
      search: "",
      CurrentYearId: 0,
      previewRecordPrompt: false,
      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",
      editRecordPrompt: false,
      CardContent: [],

      editFlag: false,

      selectedData: {},
      addRecordPrompt: false,

      select: "Item 1",
      items: ["Item 1", "Item 2", "Item 3", "Item 4"],
      checkbox: false,

      SearchId: "",
      SearchIdRules: [(v) => !!v || "Search Type is required"],

      LoadingFlag: false,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",

      // Dashboard Data - Single object that will be populated from API
      memberData: {},
    };
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    SearchId: function () {
      console.log("watch SearchId");
      this.rows = [];
    },
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = NextJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        var ZoneCode = this.$session.get("ZoneId");
        ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
        console.log({ ZoneCode });
        this.ZoneCode = ZoneCode;

        this.pageData();
        this.getNominationInfo();
      }
    },
  },
  methods: {
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    getActiveStatusColor(status) {
      // Method to get color for status chips
      switch (status) {
        case 'Active':
          return '#28a745';
        case 'Inactive':
          return '#dc3545';
        case 'Pending':
          return '#ffc107';
        default:
          return '#6c757d';
      }
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "nominate_member",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
      this.getTableRecords();
      this.getNominationInfo();
    },
    hidePreviewRecordPrompt(flag) {
      console.log("hidePreviewRecordPrompt called");
      console.log({ flag });
      this.previewRecordPrompt = false;
      if (flag) {
        this.getTableRecords();
      }
    },
    previewData(tr) {
      console.log("previewData called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.previewRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    pageData() {
      console.log("pageData called");
    },
    hideAddRecordPrompt(flag) {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      console.log(flag);
      if (flag) {
        this.getTableRecords();
      }
    },
    resetForm() {
      this.search = "";
      this.rows = [];
      this.selected = [];
      this.$refs.form3.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    hideEditRecordPrompt() {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      // console.log(flag);
      // if (flag) {
      this.getTableRecords();
      // }
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedData = tr;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one member to edit";
        this.toast("error", message);
      }
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/nomination-member/lists";
      var upload = {
        UserInterface: 1,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var flag = 0;

      this.rows = [];
      this.ResultFlag = false;
      this.LoadingFlag = true;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.ResultFlag = true;
          thisIns.LoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);

          var records = response.data.records;
          console.log({records});

          if (flag == 1) {
            thisIns.rows = records.TableData;
            thisIns.tableColumns1 = records.TableHeader;
            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          // thisIns.toast("error", error);
          thisIns.ResultFlag = true;
          thisIns.LoadingFlag = false;
        });
    },
    getNominationInfo() {
      console.log("getNominationInfo called");

      // Prevent multiple simultaneous calls
      if (this.NominationInfoLoadingFlag) {
        console.log("getNominationInfo already loading, skipping...");
        return;
      }

      this.memberDataFlag = false;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/nomination-member/statistics-info";
      var upload = {
        UserInterface: 1,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = {};
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("getNominationInfo response=" + JSON.stringify(response));

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);

          records = response.data.records;
          console.log("nomination info records:", { records });

          thisIns.memberDataFlag = flag;

          if (flag == 1) {
            // Update NominationInfo with API response
            thisIns.memberData = records
          }
        })
        .catch(function (error) {
          console.log("getNominationInfo error=" + error);
        });
    },

    deleteConfirmAlert(tr) {
      console.log("deleteConfirmAlert called");
      var message = "";
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt += "<br/> Member Name <b> " + tr.MemberName + "</b>";
        htmlTxt += "<br/> Designation Name  <b> " + tr.DesignationName + "</b>";

        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.deleteData(tr.NominationMember);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        if (n1 == 0) {
          message += "Kindly select one record to delete. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    deleteData(NominationMember) {
      console.log("deleteData is called");

      console.log("NominationMember=" + NominationMember);

      if (NominationMember > 0) {
        this.DeleteLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/nomination-member/destroy";
        var upload = {
          UserInterface: 1,
          NominationMember: NominationMember,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.DeleteLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.getTableRecords();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
            thisIns.DeleteLoadingFlag = false;
          });
      } else {
        var message = "Nomination Member Id should not be empty. ";
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
/* Legacy Styles - keeping for backward compatibility */
#staff-list {
  .v-col {
    margin-bottom: 15px;
  }

  .member-card {
    min-height: 320px;
  }

  .v-card-actions {
    flex-wrap: wrap;
    gap: 6px;
  }

  .title-text {
    font-size: 24px;
    font-weight: bold;
  }

  .subtitle-text {
    font-size: 22px;
    font-weight: 600;
  }

  .normal-text {
    font-size: 20px;
    font-weight: 500;
  }

  .member-card h4,
  .member-card h5 {
    font-size: 20px;
    margin: 5px 0;
    line-height: 1.4;
  }

  .member-card h4 b,
  .member-card h5 b {
    font-weight: bold;
  }

  .v-chip {
    font-size: 18px;
    padding: 8px 12px;
  }

  .v-btn {
    font-size: 16px;
    font-weight: bold;
  }

  .v-avatar {
    border: 3px solid #ffffff;
  }

  // Professional Nomination List Styles - Matching NominationApplyList theme
  &.professional-nomination-list {
    background-color: #f5f7fa;
    min-height: 100vh;

    .main-container {
      border-radius: 12px !important;
      overflow: hidden;
      background: white;
      box-shadow: 0 4px 20px rgba(0,0,0,0.08) !important;
      margin: 1rem;
    }

    // Professional Header Styles
    .professional-header {
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
      border-bottom: 1px solid #e3f2fd;

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        width: 100%;

        .title-section {
          flex: 1;

          .main-title-wrapper {
            display: flex;
            align-items: center;

            .title-text {
              .page-main-title {
                font-size: 2rem;
                font-weight: 700;
                color: #2c3e50;
                margin: 0;
                font-family: 'Roboto', sans-serif;
                line-height: 1.2;
              }

              .year-badge {
                margin-top: 0.5rem;
              }
            }
          }
        }

        .action-section {
          .refresh-btn {
            font-weight: 500;
            text-transform: none;
            padding: 0 24px;
            height: 40px;

            &:hover {
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(46, 204, 113, 0.3);
            }
          }
        }
      }

      .subtitle-section {
        .subtitle-wrapper {
          display: flex;
          align-items: center;
          background: rgba(25, 118, 210, 0.05);
          padding: 1rem;
          border-radius: 8px;
          border-left: 4px solid #1976d2;

          .page-description {
            font-size: 1.1rem;
            color: #424242;
            margin: 0;
            font-weight: 400;
            line-height: 1.4;
          }
        }
      }
    }

    // Professional Content Styles
    .main-content {
      background: #f5f7fa;
    }

    // Professional Loading Styles
    .loading-section {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 300px;

      .loading-wrapper {
        text-align: center;

        .loading-text {
          font-size: 1.1rem;
          color: #666;
          font-weight: 500;
          margin: 0;
        }
      }
    }

    // Dashboard Cards Section
    .dashboard-section {
      margin: 2rem 0;

      .dashboard-cards {
        margin: 0 -12px;

        .dashboard-card {
          cursor: pointer;
          transition: all 0.3s ease;
          border-radius: 12px !important;
          background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
          height: 100%;
          display: flex;
          flex-direction: column;

          .v-card__text {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }

          &:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
          }

          .card-title {
            color: #2c3e50;
            font-size: 1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            font-family: 'Roboto', sans-serif;
          }

          &.deadline-card {
            border-left: 4px solid #28a745;

            .date-info {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 8px;

              .date-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 12px 16px;
                background: rgba(40, 167, 69, 0.1);
                border-radius: 8px;
                border: 1px solid rgba(40, 167, 69, 0.2);
                transition: all 0.2s ease;

                &:hover {
                  background: rgba(40, 167, 69, 0.15);
                  transform: translateX(2px);
                }

                .date-label {
                  color: #28a745;
                  font-weight: 600;
                  font-size: 0.9rem;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                }

                .date-value {
                  color: #2c3e50;
                  font-weight: 600;
                  font-size: 0.9rem;
                }
              }
            }
          }

          &.eligibility-card {
            border-left: 4px solid #f093fb;

            .status-info {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 12px;

              .status-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 12px 16px;
                background: rgba(240, 147, 251, 0.1);
                border-radius: 8px;
                border: 1px solid rgba(240, 147, 251, 0.2);
                transition: all 0.2s ease;

                &:hover {
                  background: rgba(240, 147, 251, 0.15);
                  transform: translateX(2px);
                }

                .status-label {
                  color: #666;
                  font-weight: 600;
                  font-size: 0.9rem;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                }

                .status-chip {
                  font-weight: 600 !important;
                  font-size: 0.8rem !important;
                }
              }

              .votes-section {
                background: rgba(240, 147, 251, 0.1);
                padding: 16px;
                border-radius: 8px;
                border: 1px solid rgba(240, 147, 251, 0.2);
                text-align: center;
                transition: all 0.2s ease;

                &:hover {
                  background: rgba(240, 147, 251, 0.15);
                  transform: translateY(-1px);
                }

                .votes-label {
                  color: #666;
                  font-weight: 600;
                  display: block;
                  margin-bottom: 8px;
                  font-size: 0.9rem;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                }

                .votes-display {
                  color: #2c3e50;
                }
              }
            }
          }

          &.statistics-card {
            border-left: 4px solid #4facfe;

            .stats-info {
              display: flex;
              flex-direction: column;
              gap: 12px;
              flex: 1;

              .stat-item {
                text-align: center;
                padding: 16px 12px;
                background: rgba(79, 172, 254, 0.1);
                border-radius: 8px;
                border: 1px solid rgba(79, 172, 254, 0.2);
                transition: all 0.2s ease;

                &:hover {
                  background: rgba(79, 172, 254, 0.15);
                  transform: translateY(-1px);
                }

                .stat-number {
                  display: block;
                  font-size: 1.8rem;
                  font-weight: 700;
                  color: #4facfe;
                  line-height: 1;
                  margin-bottom: 4px;
                }

                .stat-label {
                  display: block;
                  font-size: 0.85rem;
                  color: #666;
                  font-weight: 500;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                }
              }
            }
          }
        }
      }
    }

    // Professional Info Cards Styles
    .info-sections-container {
      margin: 2rem 0;

      .info-card {
        border-radius: 12px !important;
        border: 1px solid #e0e0e0;
        transition: all 0.3s ease;
        height: 100%;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.1) !important;
        }

        .info-card-header {
          background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
          border-bottom: 1px solid #e0e0e0;
          padding: 1.5rem;

          .info-card-title {
            font-size: 1.2rem;
            font-weight: 600;
            color: #2c3e50;
            margin: 0;
          }
        }

        .info-card-content {
          padding: 1.5rem;
        }

        &.requirements-card {
          .info-card-header {
            border-left: 4px solid #e74c3c;
          }
        }

        &.process-card {
          .info-card-header {
            border-left: 4px solid #3498db;
          }
        }
      }
    }

    // Requirements List Styles
    .requirements-list {
      .requirement-item {
        display: flex;
        align-items: flex-start;
        margin-bottom: 1rem;
        padding: 1rem;
        background: #fff3f3;
        border-radius: 8px;
        border-left: 3px solid #e74c3c;
        transition: all 0.2s ease;

        &:hover {
          background: #ffebeb;
          transform: translateX(4px);
        }

        .requirement-number {
          background: #e74c3c;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          font-weight: 600;
          margin-right: 1rem;
          flex-shrink: 0;
        }

        .requirement-text {
          font-size: 0.95rem;
          color: #424242;
          line-height: 1.6;
          font-weight: 400;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    // Process Steps Styles
    .process-steps {
      .process-step {
        display: flex;
        align-items: flex-start;
        margin-bottom: 1rem;
        padding: 1rem;
        background: #f0f8ff;
        border-radius: 8px;
        border-left: 3px solid #3498db;
        transition: all 0.2s ease;

        &:hover {
          background: #e6f3ff;
          transform: translateX(4px);
        }

        .step-number {
          background: #3498db;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          font-weight: 600;
          margin-right: 1rem;
          flex-shrink: 0;
        }

        .step-text {
          font-size: 0.95rem;
          color: #424242;
          line-height: 1.6;
          font-weight: 400;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    // Applications Section
    .applications-section {
      margin: 2rem 0;

      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 32px;
        padding-bottom: 16px;
        border-bottom: 2px solid #f1f5f9;

        .section-title {
          display: flex;
          align-items: center;
          font-size: 24px;
          font-weight: 600;
          color: #1e293b;
        }

        .applications-count {
          .count-chip {
            font-size: 16px !important;
            font-weight: 600 !important;
            padding: 8px 16px !important;
          }
        }
      }

      .member-cards-container {
        .member-cards-grid {
          margin: 0 -12px;

          .member-card-col {
            padding: 12px;
          }

          // Professional Nomination Cards Styles
          .nomination-card {
            border-radius: 12px !important;
            transition: all 0.3s ease;
            border: 1px solid #e0e0e0;
            background: #ffffff;

            &:hover {
              transform: translateY(-2px);
              box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important;
              border-color: #1976d2;
            }

            .nomination-header {
              position: relative;

              &::after {
                content: '';
                position: absolute;
                bottom: -1px;
                left: 0;
                right: 0;
                height: 1px;
                background: rgba(255,255,255,0.2);
              }
            }

            .member-header {
              .member-avatar-section {
                .member-avatar {
                  border: 4px solid white !important;
                  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1) !important;
                }
              }

              .member-basic-info {
                .member-name {
                  font-size: 20px;
                  font-weight: 700;
                  color: #1e293b;
                  margin: 0 0 8px 0;
                  line-height: 1.3;
                }

                .member-id {
                  font-size: 14px;
                  color: #64748b;
                  margin: 0 0 4px 0;
                  font-weight: 500;
                }

                .member-designation {
                  font-size: 16px;
                  color: #475569;
                  margin: 0;
                  font-weight: 500;
                }
              }
            }

            .member-status-section {
              .status-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 12px;
                padding: 12px 16px;
                background: #f8fafc;
                border-radius: 8px;
                border-left: 4px solid #e2e8f0;

                .status-label {
                  font-size: 14px;
                  font-weight: 600;
                  color: #374151;
                }

                .status-chip {
                  font-size: 12px !important;
                  font-weight: 600 !important;
                  border-radius: 6px !important;
                }
              }
            }

            .member-actions {
              .action-buttons-row {
                gap: 0.25rem;
              }

              .action-btn {
                min-width: unset !important;
                padding: 0 12px !important;
                font-size: 0.8rem !important;
                font-weight: 500 !important;
                text-transform: none !important;
                letter-spacing: 0.3px !important;
                border-radius: 6px !important;
                transition: all 0.2s ease !important;

                &:hover {
                  transform: translateY(-1px);
                  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
                }

                .v-icon {
                  margin-right: 4px !important;
                }

                &.edit-btn {
                  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%) !important;
                }

                &.delete-btn {
                  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
                }

                &.approve-btn {
                  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
                }
              }
            }
          }
        }
      }

      .empty-state {
        text-align: center;
        padding: 80px 40px;

        .empty-content {
          .empty-title {
            font-size: 24px;
            font-weight: 600;
            color: #6b7280;
            margin: 16px 0 8px 0;
          }

          .empty-message {
            font-size: 16px;
            color: #9ca3af;
            margin: 0;
          }
        }
      }
    }
  }
}

// Responsive Design
@media (max-width: 1200px) {
  #staff-list.professional-nomination-list {
    .main-container {
      margin: 0.5rem;
    }

    .professional-header {
      .header-content {
        .title-section .main-title-wrapper .title-text .page-main-title {
          font-size: 1.75rem;
        }
      }
    }

    .info-sections-container {
      .info-card {
        margin-bottom: 1.5rem;
      }
    }
  }
}

@media (max-width: 768px) {
  #staff-list.professional-nomination-list {
    .main-container {
      margin: 0.5rem;
      border-radius: 8px !important;
    }

    .professional-header {
      padding: 1rem !important;

      .header-content {
        flex-direction: column;
        gap: 1rem;

        .title-section .main-title-wrapper {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.75rem;

          .title-text .page-main-title {
            font-size: 1.5rem;
          }
        }

        .action-section {
          align-self: flex-start;
          display: flex;
          gap: 0.5rem;
        }

        .subtitle-section .subtitle-wrapper {
          padding: 0.75rem;

          .page-description {
            font-size: 1rem;
          }
        }
      }
    }

    .main-content {
      padding: 1rem !important;
    }

    .applications-section {
      .section-header {
        flex-direction: column;
        gap: 16px;
        text-align: center;

        .section-title {
          font-size: 20px;
        }
      }

      .member-cards-container {
        .member-cards-grid {
          margin: 0 -8px;

          .member-card-col {
            padding: 8px;
          }
        }
      }
    }

    .requirements-list .requirement-item,
    .process-steps .process-step {
      padding: 0.75rem;

      .requirement-text,
      .step-text {
        font-size: 0.9rem;
      }
    }
  }
}

@media (max-width: 480px) {
  #staff-list.professional-nomination-list {
    .professional-header {
      .header-content .title-section .main-title-wrapper .title-text .page-main-title {
        font-size: 1.3rem;
        line-height: 1.3;
      }

      .action-section {
        flex-direction: column;
        width: 100%;

        .refresh-btn {
          width: 100%;
          max-width: 200px;
        }
      }
    }

    .requirements-list .requirement-item,
    .process-steps .process-step {
      flex-direction: column;

      .requirement-number,
      .step-number {
        margin-bottom: 0.5rem;
        margin-right: 0;
        align-self: flex-start;
      }
    }

    .info-card .info-card-header {
      padding: 1rem;

      .info-card-title {
        font-size: 1.1rem;
      }
    }

    .info-card .info-card-content {
      padding: 1rem;
    }

    .applications-section {
      .member-cards-container {
        .nomination-card {
          .member-actions {
            .action-buttons-row {
              flex-direction: column;
              align-items: center;

              .action-btn {
                width: 100%;
                max-width: 150px;
              }
            }
          }
        }
      }
    }
  }
}
</style>